import { HttpResponse } from '@angular/common/http';

export const FileSizeUnits = ['Bytes', 'KB', 'MB', 'GB', 'TB'] as const;

export class FileService {
    /**
     * Convert bytes to a human-readable format
     * @returns {string} The human-readable format
     * @example FileService.toBytes(1024) // 1 KB
     */
    public static bytesToFileSizes(bytes: number): `${number} ${(typeof FileSizeUnits)[number]}` {
        if (bytes === 0) {
            return '0 Bytes';
        }

        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const size = parseFloat((bytes / Math.pow(1024, i)).toFixed(2));

        return `${size} ${FileSizeUnits[i]}`;
    }

    public static calculateTotalFileSize(files: File[]): number {
        return files.reduce((acc: number, file: File) => acc + file.size, 0);
    }

    public static convertBase64ToBlob(base64: string): Blob {
        const mimeType = base64.split(',')[0].split(';')[0].split(':')[1];

        const binaryString = atob(base64.split(',')[1]);
        const characters = binaryString.split('');

        const array = new Uint8Array(characters.map((char: string) => char.charCodeAt(0)));

        return new Blob([array], { type: mimeType });
    }

    public static convertToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = (): void => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    public static downloadFile(response: HttpResponse<Blob>): void {
        const contentDispositionHeader = FileService.getContentDispositionHeaderName(response);
        if (!response.body || !contentDispositionHeader) {
            return;
        }

        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(response.body);
        downloadLink.download = contentDispositionHeader;
        downloadLink.click();
    }

    /**
     * Convert a file size to bytes
     * @param size The size to convert
     * @param fileSize The file size unit
     * @returns The size in bytes
     */
    public static fileSizeToBytes(size: number, fileSize: (typeof FileSizeUnits)[number]): number {
        const unitIndex = FileSizeUnits.indexOf(fileSize);
        if (unitIndex === -1) {
            throw new Error('Invalid file size unit');
        }

        return size * Math.pow(1024, unitIndex);
    }

    public static getContentDispositionHeaderName(response: HttpResponse<Blob>): string | null {
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (!contentDispositionHeader) {
            return null;
        }

        const attachment = contentDispositionHeader.split(';')[1]; // exampleValue : 'filename="audit-pbo-gironde.zip"'
        const filename = attachment.trim().split('=')[1];

        return filename.replaceAll('"', '');
    }
}
